// ==================== B2B Module Type Definitions v2.0 ====================
// src/types/b2b.ts

// ── Enums ──
export type B2BPurpose = 'brand' | 'product' | 'booking' | 'sales' | 'recruitment' | 'other';
export type B2BAudience = 'b2b' | 'consumer' | 'investor' | 'partner' | 'jobseeker' | 'other';
export type B2BPage = 'hero' | 'about' | 'products' | 'cases' | 'contact' | 'blog' | 'faq' | 'privacy';
export type B2BCTA = 'form' | 'booking' | 'download' | 'purchase' | 'subscribe' | 'other';
export type B2BIndustry = 'tech' | 'manufacturing' | 'food' | 'medical' | 'education' | 'finance' | 'retail' | 'consulting' | 'realestate' | 'other';
export type B2BStatus = 'draft' | 'strategy' | 'generating' | 'completed' | 'error' | 'cancelled';
export type StreamEventType = 'text' | 'code' | 'error' | 'system' | 'done' | 'strategy';

// ── Industry Descriptions ──
export const INDUSTRY_LABELS: Record<B2BIndustry, string> = {
  tech: '資訊科技 / 軟體',
  manufacturing: '製造業 / 工業',
  food: '餐飲 / 食品',
  medical: '醫療 / 健康',
  education: '教育 / 培訓',
  finance: '金融 / 保險',
  retail: '零售 / 電商',
  consulting: '顧問 / 專業服務',
  realestate: '房地產 / 建築',
  other: '其他',
};

// ── Step 0: Company Identity ──
export interface Q0Identity {
  companyName: string;
  industry: B2BIndustry;
  description: string; // 一句話描述
  foundedYear?: string;
  teamSize?: string;
}

// ── Step 1: Purpose ──
export interface Q1Purpose {
  type: B2BPurpose;
  otherText?: string;
}

// ── Step 2: Audience ──
export interface Q2Audience {
  types: B2BAudience[];
  otherText?: string;
}

// ── Step 3: Products & Selling Points ──
export interface Q3Products {
  coreProduct: string; // 核心產品/服務描述
  sellingPoints: [string, string, string]; // 3個主要賣點
  advantage: string; // 與競爭對手相比的優勢
}

// ── Step 4: Assets & Visuals ──
export interface Q4Assets {
  logo?: string; // data:image/...;base64,...
  images: string[]; // data:image/...;base64,...[]
  brandColors: {
    primary: string; // #RRGGBB
    secondary: string;
    accent: string;
    text: string;
  };
  unsplashKeywords: string; // e.g. "modern office, tech team, data dashboard"
}

// ── Step 5: Pages ──
// q5_pages: B2BPage[] (direct array)

// ── Step 6: Contact & CTA ──
export interface Q6Contact {
  address?: string;
  phone?: string;
  email?: string;
  socialMedia?: string;
  ctaType: B2BCTA;
  ctaOtherText?: string;
}

// ── Complete Answers ──
export interface B2BAnswers {
  q0_identity: Q0Identity;
  q1_purpose: Q1Purpose;
  q2_audience: Q2Audience;
  q3_products: Q3Products;
  q4_assets: Q4Assets;
  q5_pages: B2BPage[];
  q6_contact: Q6Contact;
}

// ── Strategy Report ──
export interface B2BStrategyReport {
  siteStructure: string; // 網站結構建議
  copyDirection: string; // 文案方向
  seoKeywords: string[]; // SEO 關鍵詞
  conversionPath: string; // 轉化路徑建議
  designNotes: string; // 設計建議
}

// ── Stream ──
export interface StreamChunk {
  type: StreamEventType;
  content: string;
  timestamp: number; // Date.now()
}

export interface B2BParsedFile {
  path: string; // e.g., "app/page.tsx"
  content: string;
  language: 'typescript' | 'tsx' | 'css' | 'json' | 'markdown';
}

// ── Session ──
export interface B2BSession {
  id: string; // UUID v4
  status: B2BStatus;
  answers: B2BAnswers;
  systemPrompt: string;
  strategyPrompt: string;
  strategyReport: B2BStrategyReport | null;
  generatedCode: string;
  parsedFiles: B2BParsedFile[];
  streamLog: StreamChunk[];
  error?: string;
  createdAt: string; // ISO 8601
  updatedAt: string;
}

// ── Zustand Store Extensions ──
export interface B2BState {
  b2bSession: B2BSession | null;
  b2bCurrentStep: number; // 0-6
}

export interface B2BActions {
  initB2BSession: () => void;
  setB2BAnswers: (answers: Partial<B2BAnswers>) => void;
  setB2BStep: (step: number) => void;
  setB2BStatus: (status: B2BStatus) => void;
  setB2BStrategyReport: (report: B2BStrategyReport) => void;
  appendStreamChunk: (chunk: StreamChunk) => void;
  setB2BGeneratedFiles: (files: B2BParsedFile[]) => void;
  setB2BSystemPrompt: (prompt: string) => void;
  setB2BError: (error: string) => void;
  resetB2BSession: () => void;
  cancelB2BGeneration: () => void;
}

// ── Worker API Types ──
export interface B2BStreamRequest {
  sessionId: string;
  answers: B2BAnswers;
  stream: boolean;
}

export interface B2BStrategyRequest {
  sessionId: string;
  answers: B2BAnswers;
}

export interface B2BStrategyResponse {
  sessionId: string;
  status: 'started' | 'error';
  streamUrl?: string;
  error?: string;
}

export interface B2BStreamResponse {
  sessionId: string;
  status: 'started' | 'error';
  streamUrl?: string;
  error?: string;
}

export interface B2BStatusResponse {
  sessionId: string;
  status: B2BStatus;
  progress?: number; // 0-100
  currentFile?: string;
  error?: string;
}

// ── Default Values ──
export function getDefaultB2BAnswers(): B2BAnswers {
  return {
    q0_identity: {
      companyName: '',
      industry: 'tech',
      description: '',
    },
    q1_purpose: { type: 'brand' },
    q2_audience: { types: ['b2b'] },
    q3_products: {
      coreProduct: '',
      sellingPoints: ['', '', ''],
      advantage: '',
    },
    q4_assets: {
      images: [],
      brandColors: {
        primary: '#6366F1',
        secondary: '#8B5CF6',
        accent: '#10B981',
        text: '#0F172A',
      },
      unsplashKeywords: '',
    },
    q5_pages: ['hero'],
    q6_contact: {
      ctaType: 'form',
    },
  };
}

export function createB2BSession(): B2BSession {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    status: 'draft',
    answers: getDefaultB2BAnswers(),
    systemPrompt: '',
    strategyPrompt: '',
    strategyReport: null,
    generatedCode: '',
    parsedFiles: [],
    streamLog: [],
    createdAt: now,
    updatedAt: now,
  };
}
